class ProductUpsell extends HTMLElement {
    // Constants
    static CLASSES = {
      LOADED: 'product-upsell--loaded',
      SELECT: 'product__upsell__select',
      ITEM: 'product__upsell__item',
      LOADING: 'button--loading'
    };
  
    static SELECTORS = {
      FORM: 'form',
      VARIANT_SELECT: 'select.product__upsell__select',
      VARIANT_INPUT: 'input[name="id"]',
      SUBMIT_BUTTON: 'button[type="submit"]',
      ITEM: '.product__upsell__item'
    };
  
    static MESSAGES = {
      NO_SIZE: 'Veuillez sélectionner une taille',
      ERROR_GENERIC: 'Une erreur est survenue. Veuillez réessayer.',
      ERROR_ADD_TO_CART: 'Erreur lors de l\'ajout au panier'
    };
  
    static INTERSECTION_ROOT_MARGIN = '0px 0px 400px 0px';
    static SUCCESS_FEEDBACK_DELAY = 2000;
    static CLASS_REPLACEMENT_PATTERN = /product-recommendation__/g;
    static CLASS_REPLACEMENT_VALUE = 'product__upsell__';
  
    constructor() {
      super();
      this.observer = null;
    }
  
    connectedCallback() {
      if (this.hasAttribute('data-initialized')) return;
      this.setAttribute('data-initialized', 'true');
      this.setupIntersectionObserver();
    }
  
    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  
    setupIntersectionObserver() {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        { rootMargin: ProductUpsell.INTERSECTION_ROOT_MARGIN }
      );
      this.observer.observe(this);
    }
  
    handleIntersection(entries) {
      if (!entries[0].isIntersecting) return;
      this.observer.unobserve(this);
  
      if (this.classList.contains(ProductUpsell.CLASSES.LOADED)) return;
  
      const url = this.dataset.url;
      if (!url) {
        console.error('ProductUpsell: No URL provided');
        this.remove();
        return;
      }
  
      this.loadContent(url);
    }
  
    async loadContent(url) {
      try {
        const response = await fetch(url);
        const text = await response.text();
        const processedContent = this.processFetchedContent(text);
  
        if (!processedContent) {
          this.remove();
          return;
        }
  
        this.innerHTML = processedContent;
        this.classList.add(ProductUpsell.CLASSES.LOADED);
        this.initializeForm();
      } catch (error) {
        console.error('Error fetching product recommendations:', error);
        this.remove();
      }
    }
  
    processFetchedContent(htmlText) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlText;
  
      const section = tempDiv.querySelector('section');
      if (!section) return null;
  
      const hasRecommendations = section.querySelector(ProductUpsell.SELECTORS.ITEM);
      if (!hasRecommendations) return null;
  
      let content = section.innerHTML;
      content = this.removeNestedUpsells(content);
      content = this.replaceClassNames(content);
  
      return content;
    }
  
    removeNestedUpsells(content) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      const nestedUpsells = tempDiv.querySelectorAll('product-upsell');
  
      nestedUpsells.forEach(el => {
        const fragment = document.createDocumentFragment();
        while (el.firstChild) {
          fragment.appendChild(el.firstChild);
        }
        el.replaceWith(fragment);
      });
  
      return tempDiv.innerHTML;
    }
  
    replaceClassNames(content) {
      return content.replace(
        ProductUpsell.CLASS_REPLACEMENT_PATTERN,
        ProductUpsell.CLASS_REPLACEMENT_VALUE
      );
    }
  
    initializeForm() {
      const form = this.querySelector(ProductUpsell.SELECTORS.FORM);
      if (!form) {
        console.error('ProductUpsell: Form not found');
        return;
      }
  
      const variantSelect = this.querySelector(ProductUpsell.SELECTORS.VARIANT_SELECT);
      const variantIdInput = this.querySelector(ProductUpsell.SELECTORS.VARIANT_INPUT);
      const addButton = this.querySelector(ProductUpsell.SELECTORS.SUBMIT_BUTTON);
  
      if (!addButton) {
        console.error('ProductUpsell: Add button not found');
        return;
      }
  
      if (variantSelect) {
        variantSelect.addEventListener('change', () => {
          this.handleVariantChange(variantSelect, variantIdInput, addButton);
        });
      }
  
      const handleSubmit = (e) => {
        e.preventDefault();
        if (!this.validateVariantSelection(variantIdInput)) return;
        this.handleAddToCart(form, addButton);
      };
  
      form.addEventListener('submit', handleSubmit);
      addButton.addEventListener('click', (e) => {
        if (addButton.hasAttribute('form')) {
          e.preventDefault();
          handleSubmit(e);
        }
      });
    }
  
    handleVariantChange(variantSelect, variantIdInput, addButton) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      const variantId = selectedOption.value;
  
      if (variantIdInput) {
        variantIdInput.value = variantId;
      }
  
      addButton.disabled = false;
    }
  
    validateVariantSelection(variantIdInput) {
      if (variantIdInput && !variantIdInput.value) {
        console.error('ProductUpsell: No variant ID selected -', ProductUpsell.MESSAGES.NO_SIZE);
        return false;
      }
      return true;
    }
  
    getVariantId(form) {
      const variantIdInput = form.querySelector(ProductUpsell.SELECTORS.VARIANT_INPUT);
      let variantId = variantIdInput?.value;
  
      if (!variantId) {
        const formData = new FormData(form);
        variantId = formData.get('id');
      }
  
      return variantId;
    }
  
    setButtonLoadingState(button, isLoading) {
      button.disabled = isLoading;
      if (isLoading) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add(ProductUpsell.CLASSES.LOADING);
      } else {
        button.removeAttribute('aria-disabled');
        button.classList.remove(ProductUpsell.CLASSES.LOADING);
      }
    }
  
    prepareFormData(form, variantId) {
      const formData = new FormData(form);
      formData.delete('id');
      formData.set('id', variantId);
  
      const cart = document.querySelector('w3-cart');
      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map(section => section.id));
        formData.append('sections_url', window.location.pathname);
      }
      formData.append('sections_url', window.location.pathname);
  
      return formData;
    }
  
    async handleAddToCart(form, button) {
      const variantId = this.getVariantId(form);
      if (!variantId) {
        console.error('ProductUpsell:', ProductUpsell.MESSAGES.NO_SIZE);
        return;
      }
  
      this.setButtonLoadingState(button, true);
  
      try {
        const formData = this.prepareFormData(form, variantId);
        const response = await this.addToCart(formData);
  
        if (response.status) {
          throw new Error(response.description || ProductUpsell.MESSAGES.ERROR_ADD_TO_CART);
        }
  
        this.handleCartSuccess(response, button);
      } catch (error) {
        this.handleCartError(error, button);
      }
    }
  
    async addToCart(formData) {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: formData
      });
  
      return response.json();
    }
  
    handleCartSuccess(response, button) {
      const cart = document.querySelector('w3-cart');
      if (cart) {
        cart.updateCart(response);
      }
  
      const cartDrawerEvent = new CustomEvent('theme:drawer:cart:open');
      document.dispatchEvent(cartDrawerEvent);
  
      setTimeout(() => {
        this.setButtonLoadingState(button, false);
      }, ProductUpsell.SUCCESS_FEEDBACK_DELAY);
    }
  
    handleCartError(error, button) {
      console.error('ProductUpsell: Error adding to cart:', error);
      console.error('ProductUpsell:', error.message || ProductUpsell.MESSAGES.ERROR_GENERIC);
      this.setButtonLoadingState(button, false);
    }
  }
  
  customElements.define('product-upsell', ProductUpsell);